import { ButtonHTMLAttributes, AnchorHTMLAttributes, CSSProperties, forwardRef } from 'react';
import clsx from 'clsx';

import styles from './Button.module.scss';
import Link from 'next/link';

export type ButtonVariantsType =
  | 'primary'
  | 'secondary'
  | 'tertiary'
  | 'table-primary'
  | 'table-secondary'
  | 'table-tertiary'
  | 'text-primary'
  | 'text-secondary'
  | 'upload'
  | 'find';

interface CommonButtonProps extends Pick<CSSProperties, 'width' | 'maxWidth'> {
  variants?: ButtonVariantsType;
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'wide';
  typeIcon?: boolean;
  disabled?: boolean;
  className?: string;
}

interface ButtonTagProps extends CommonButtonProps, ButtonHTMLAttributes<HTMLButtonElement> {
  tag?: 'button';
  type?: 'button' | 'submit' | 'reset';
}

interface AnchorTagProps extends CommonButtonProps, AnchorHTMLAttributes<HTMLAnchorElement> {
  tag?: 'a';
  href: string;
}

export type ButtonProps = ButtonTagProps | AnchorTagProps;

const Button = forwardRef<HTMLButtonElement | HTMLAnchorElement, ButtonProps>((props, ref) => {
  const {
    tag = 'button',
    typeIcon = false,
    className,
    variants = !typeIcon ? 'primary' : '',
    size = !typeIcon ? 'md' : '',
    children,
    disabled = false,
    ...rest
  } = props;

  const classes = clsx(
    styles.button,
    styles[variants],
    styles[size],
    className,
    typeIcon ? styles.typeIcon : '',
  );

  if (tag === 'a') {
    const { href, ...anchorProps } = rest as AnchorTagProps;
    return (
      <Link href={href} passHref legacyBehavior>
        <a ref={ref as React.Ref<HTMLAnchorElement>} className={classes} {...anchorProps}>
          {children}
        </a>
      </Link>
    );
  }

  const { type = 'button', ...buttonProps } = rest as ButtonTagProps;

  return (
    <button
      ref={ref as React.Ref<HTMLButtonElement>}
      className={classes}
      type={type}
      disabled={disabled}
      {...buttonProps}
    >
      {children}
    </button>
  );
});

export default Button;
